import { BookingAddressPicker } from '@components/Address/BookingAddressPicker';
import { getMobileBookingProgress } from '@components/BookingFlow/Common/bookingFlowProgress';
import { BookingScreen } from '@components/BookingFlow/Mobile/BookingScreen';
import { ExplainMobileTireServiceBooking } from '@components/BookingFlow/Mobile/ExplainMobileTireServiceBooking';
import { AddressStepLogin } from '@components/Login/AddressStepLogin';
import { t } from '@lingui/core/macro';
import { brands } from 'noddi-types';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiDialog } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import {
  commonTrackingEvents,
  trackBookingFlowStarted,
  tracking,
  useBookingActions,
  useBookingAddress,
  useIsAddressStepValid
} from 'noddi-web';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileBookingCustomer } from '../../../../hooks/useMobileBookingCustomer';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';

export const Address = () => {
  const { setSelectedAddress } = useBookingActions();
  const selectedAddress = useBookingAddress();
  const isAddressStepValid = useIsAddressStepValid();
  const navigate = useNavigate();
  const { currentBrand } = useCurrentBrand();
  const [isExplainMobileServiceModalOpen, setIsExplainMobileServiceModalOpen] = useState(false);
  const { userGroupId } = useMobileBookingCustomer();

  // Address is the first step of the wizard — funnel top.
  useEffect(() => {
    trackBookingFlowStarted({ brand: currentBrand, locationType: 'mobile' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentBrand omitted: funnel-start event fires once on mount
  }, []);

  const showNewServiceCard = currentBrand === brands.tronderdekk && !selectedAddress;

  const navigateToNextStep = () => {
    tracking.track(commonTrackingEvents.addressStepCompleted, { brand: currentBrand });
    navigate(customerRoutes.newBookingCars.getPath());
  };

  return (
    <BookingScreen
      progress={getMobileBookingProgress('address')}
      title={t`Where do you need our help?`}
      onNextClicked={navigateToNextStep}
      disableNextButton={!isAddressStepValid}
      validationVariant='addressRequired'
      hideShoppingCart
      hideBackButton
    >
      <>
        <div className='flex flex-col gap-2'>
          <BookingAddressPicker
            userGroupId={userGroupId}
            setSelectedAddress={setSelectedAddress}
            selectedAddress={selectedAddress}
            onSavedAddressSelected={() => navigateToNextStep()}
            // Fires only for an address inside the delivery area; out-of-area keeps the customer here.
            onAddressCreationSuccess={() => navigateToNextStep()}
          />
          {showNewServiceCard && (
            <NoddiBasicCard
              className='flex items-center justify-between px-4 py-2'
              onClick={() => setIsExplainMobileServiceModalOpen(true)}
            >
              <div className='flex flex-col'>
                <div className='flex items-center gap-2'>
                  <NoddiIcon name='Star' size='xSmall' className='fill-yellow-300' />
                  <p className='text-sm font-semibold'>{t`New Service!`}</p>
                </div>
                <p className='text-sm text-muted-foreground'>{t`This is how it works`}</p>
              </div>
              <NoddiIcon name='ChevronRight' size='xSmall' />
            </NoddiBasicCard>
          )}
          <AddressStepLogin />
        </div>
        <NoddiDialog
          open={isExplainMobileServiceModalOpen}
          onClose={() => setIsExplainMobileServiceModalOpen(false)}
          title={t`New Service!`}
          description={t`This is how it works`}
          className='max-h-[80vh]'
        >
          <ExplainMobileTireServiceBooking />
        </NoddiDialog>
      </>
    </BookingScreen>
  );
};
